<script lang="ts">
  import { onDestroy } from 'svelte';
  import { api, navigate, type Me } from '../lib/api';
  import { AudioOut } from '../lib/audio';
  import { keepAwake, openMic } from '../lib/device';
  import { Session } from '../lib/session.svelte';
  import { describeTarget } from '../../../shared/src/permissions';
  import type { ParticipantInfo, PositionConfig, TalkTarget } from '../../../shared/src/types';
  import ConnBadge from '../components/ConnBadge.svelte';
  import PttButton from '../components/PttButton.svelte';
  import StartGate from '../components/StartGate.svelte';
  import VideoView from '../components/VideoView.svelte';

  let { principal }: { principal: NonNullable<Me['principal']> } = $props();

  let session = $state<Session | null>(null);
  let micError = $state<string | null>(null);
  const saved = JSON.parse(localStorage.getItem('vtb.director') ?? '{}');
  let talkVol = $state<number>(saved.talkVol ?? 1);
  let thumbs = $state<boolean>(saved.thumbs ?? false);
  let target = $state<TalkTarget>({ kind: 'all' });
  let latched = $state(false);

  $effect(() => {
    localStorage.setItem('vtb.director', JSON.stringify({ talkVol, thumbs }));
    session?.audio.setVolume('talk', talkVol);
  });

  async function start() {
    const audio = new AudioOut();
    audio.unlock();
    audio.setVolume('talk', talkVol);
    let mic: MediaStream | null = null;
    try {
      mic = await openMic();
    } catch (err) {
      micError = (err as Error).message;
    }
    void keepAwake(true);
    session = new Session({ positionId: null, mic, audio });
    (window as unknown as { __vtb: Session }).__vtb = session; // debugging / automated tests
    void session.start();
  }

  onDestroy(() => {
    session?.stop();
    void keepAwake(false);
  });

  // Thumbnails: subscribe to each distinct source in use (low layer when configured).
  $effect(() => {
    if (!session || session.status !== 'connected') return;
    const ids = thumbs ? [...new Set(session.positions.map((p) => p.sourceId).filter((x): x is string => !!x))] : [];
    // Live browser cameras (commentators, other directors) are always shown in the call strip.
    for (const id of callIds) if (!ids.includes(id)) ids.push(id);
    void session.watchThumbs(ids);
  });

  const byPosition = $derived.by(() => {
    const m = new Map<string, ParticipantInfo[]>();
    for (const p of session?.participants ?? []) {
      if (!p.positionId) continue;
      m.set(p.positionId, [...(m.get(p.positionId) ?? []), p]);
    }
    return m;
  });
  const callIds = $derived((session?.browserSources ?? []).filter((b) => b.live && b.id !== session?.videoSourceId).map((b) => b.id));
  async function toggleWebcam() {
    if (!session) return;
    if (session.cameraOn) await session.stopCamera();
    else await session.startCamera().catch(() => undefined);
  }
  const talkingIds = $derived(new Set((session?.talkers ?? []).map((t) => t.id)));
  const operatorCalls = $derived((session?.calls ?? []).filter((c) => c.to.kind === 'directors'));
  const myCalls = $derived((session?.calls ?? []).filter((c) => c.fromId === session?.me?.id));
  const mocks = $derived((session?.providers ?? []).filter((p) => p.kind === 'mock'));
  const posName = (id: string) => session?.positions.find((p) => p.id === id)?.name ?? 'position';

  function talk(active: boolean, t: TalkTarget = target) {
    void session?.setTalking(active, t);
  }

  function quality(list: ParticipantInfo[]): { cls: string; text: string } {
    const s = list.map((p) => p.stats).filter((x) => !!x);
    if (!s.length) return { cls: '', text: '' };
    const rtt = Math.max(...s.map((x) => x!.rttMs ?? 0));
    const loss = Math.max(...s.map((x) => x!.lossPct ?? 0));
    const lat = s.map((x) => x!.measuredLatencyMs).filter((x): x is number => x !== null);
    const cls = loss > 2 || rtt > 50 ? 'bad' : loss > 0.5 || rtt > 20 ? 'warn' : 'ok';
    return { cls, text: `${Math.round(rtt)} ms · ${loss}%${lat.length ? ` · ${Math.max(...lat)} ms g2g` : ''}` };
  }

  function callFor(posId: string) {
    return (session?.calls ?? []).find((c) => c.to.kind === 'directors' && c.fromPositionId === posId);
  }

  async function callPosition(p: PositionConfig) {
    const existing = myCalls.find((c) => c.to.kind === 'position' && c.to.positionId === p.id);
    if (existing) await session?.callAck(existing.fromId);
    else await session?.call(true, { kind: 'position', positionId: p.id });
  }

  async function mock(id: string, cmd: Record<string, unknown>) {
    await api.post(`/api/tally/${id}/mock`, cmd).catch(() => undefined);
  }

  async function logout() {
    session?.stop();
    await api.post('/api/logout');
    location.href = '/';
  }
</script>

{#if !session}
  <StartGate title="Director" subtitle={principal.name} onstart={start} />
{:else}
  <div class="dir">
    <header class="row">
      <h2 class="grow">Director</h2>
      <ConnBadge {session} />
      <label class="inline vol">🔊 <input type="range" min="0" max="1" step="0.01" bind:value={talkVol} /></label>
      <label class="inline"><input type="checkbox" bind:checked={thumbs} /> Thumbnails</label>
      {#if session.videoSourceId}
        <button class:camOn={session.cameraOn} onclick={toggleWebcam}>{session.cameraOn ? '📷 Webcam on' : '📷 Webcam'}</button>
      {/if}
      <button onclick={() => navigate('/join')}>Cameras</button>
      {#if principal.role === 'admin'}<button onclick={() => navigate('/admin')}>Admin</button>{/if}
      <button onclick={logout}>Log out</button>
    </header>
    {#if micError}<p class="error">{micError} — listen only</p>{/if}
    {#if session.cameraError}<p class="error">Camera: {session.cameraError}</p>{/if}

    {#if callIds.length || session.localVideo}
      <section class="panel">
        <h3>Video call</h3>
        <div class="callStrip">
          {#each callIds as id (id)}
            {@const b = session.browserSources.find((x) => x.id === id)}
            <div class="callTile">
              <VideoView stream={session.thumbStreams[id] ?? null} />
              <span class="cname">{b?.name ?? 'Camera'}{b?.hasAudio ? '' : ' (no audio)'}</span>
            </div>
          {/each}
          {#if session.localVideo}
            <div class="callTile self">
              <VideoView stream={session.localVideo} fit="cover" />
              <span class="cname">You</span>
            </div>
          {/if}
        </div>
      </section>
    {/if}

    <section class="talk panel">
      <div class="targets row">
        <span class="muted">Talk to:</span>
        <button class:active={target.kind === 'all'} onclick={() => (target = { kind: 'all' })}>All</button>
        {#each session.groups as g (g)}
          <button class:active={target.kind === 'group' && target.group === g} onclick={() => (target = { kind: 'group', group: g })}>{g}</button>
        {/each}
        {#if target.kind === 'position'}<button class="active">{posName(target.positionId)}</button>{/if}
      </div>
      <div class="row">
        {#if session.canTalk && session.micLive}
          <PttButton onchange={(a) => talk(a)} active={session.talking} bind:latched sublabel={describeTarget(target, posName)} />
        {/if}
        <div class="talkers grow">
          {#each session.talkers as t (t.id)}
            <div class="talker">🎙 {t.name}{t.target ? ` → ${describeTarget(t.target, posName)}` : ''}</div>
          {:else}
            <div class="muted">Nobody is talking</div>
          {/each}
        </div>
        <div class="calls">
          {#each operatorCalls as c (c.fromId)}
            <button class="callIn flash" onclick={() => session?.callAck(c.fromId)}>📣 {c.fromName} calling</button>
          {/each}
        </div>
      </div>
    </section>

    <section class="grid">
      {#each session.positions as p (p.id)}
        {@const t = session.tally[p.id]?.state ?? 'off'}
        {@const clients = byPosition.get(p.id) ?? []}
        {@const q = quality(clients)}
        {@const health = p.sourceId ? session.sources[p.sourceId] : undefined}
        {@const call = callFor(p.id)}
        {@const myCall = myCalls.find((c) => c.to.kind === 'position' && c.to.positionId === p.id)}
        <div class="tile t-{t}" class:calling={!!call} class:selected={target.kind === 'position' && target.positionId === p.id}>
          <div class="tileHead row">
            <span class="num">{p.number}</span>
            <span class="pname grow">{p.name}</span>
            {#if t === 'program'}<span class="badge program">PGM</span>{/if}
            {#if t === 'preview'}<span class="badge preview">PVW</span>{/if}
            {#if t === 'unknown'}<span class="badge warn">NO TALLY</span>{/if}
          </div>
          {#if thumbs && p.sourceId}
            <div class="thumb">
              <VideoView stream={session.thumbStreams[p.sourceId] ?? null} fit="cover" />
              {#if health && !health.signal}<div class="ns">NO SIGNAL</div>{/if}
            </div>
          {/if}
          <div class="meta">
            {#if clients.length}
              <span class="badge ok">{clients.length} online</span>
              {#each clients as c (c.id)}
                <span class="who" class:talking={talkingIds.has(c.id)}>{talkingIds.has(c.id) ? '🎙 ' : ''}{c.name}{c.lowBandwidth ? ' (low)' : ''}</span>
              {/each}
            {:else}
              <span class="badge bad">offline</span>
            {/if}
          </div>
          <div class="meta muted small">
            {health ? (health.signal ? `${health.width}×${health.height} · ${health.videoKbps} kbps` : 'source: NO SIGNAL') : p.sourceId ? 'source stopped' : 'no source'}
            {#if q.text}<span class="q {q.cls}">{q.text}</span>{/if}
          </div>
          {#if call}<button class="callIn flash" onclick={() => session?.callAck(call.fromId)}>📣 CALLING — answer</button>{/if}
          <div class="row actions">
            {#if session.canTalk && session.micLive}
              <PttButton
                size="small"
                keyboard={false}
                label="TALK"
                active={session.talking && session.talkTarget?.kind === 'position' && session.talkTarget.positionId === p.id}
                onchange={(a) => talk(a, { kind: 'position', positionId: p.id })}
              />
            {/if}
            <button class="small" onclick={() => (target = { kind: 'position', positionId: p.id })}>Select</button>
            <button class="small" class:active={!!myCall} onclick={() => callPosition(p)}>{myCall ? 'Calling…' : 'Call'}</button>
          </div>
        </div>
      {/each}
    </section>

    <section class="panel">
      <h3>Tally</h3>
      {#each session.providers as pr (pr.id)}
        <div class="row prov">
          <span class="badge {pr.connected ? 'ok' : 'bad'}">{pr.connected ? 'CONNECTED' : 'DISCONNECTED'}</span>
          <b>{pr.name}</b><span class="muted">{pr.detail}</span>
          <span class="muted">PGM {pr.program.join(', ') || '–'} · PVW {pr.preview.join(', ') || '–'}</span>
        </div>
      {:else}
        <p class="muted">No tally providers configured.</p>
      {/each}
      {#each mocks as m (m.id)}
        {@const n = Math.max(4, ...m.program.map(Number), ...m.preview.map(Number))}
        <div class="mock">
          <div class="row">
            <span class="muted">PGM</span>
            {#each Array.from({ length: n }, (_, i) => String(i + 1)) as i (i)}
              <button class="small" class:pgmOn={m.program.includes(i)} onclick={() => mock(m.id, { program: i })}>{i}</button>
            {/each}
          </div>
          <div class="row">
            <span class="muted">PVW</span>
            {#each Array.from({ length: n }, (_, i) => String(i + 1)) as i (i)}
              <button class="small" class:pvwOn={m.preview.includes(i)} onclick={() => mock(m.id, { preview: i })}>{i}</button>
            {/each}
          </div>
          <div class="row">
            <button class="primary" onclick={() => mock(m.id, { cut: true })}>CUT</button>
            <button onclick={() => mock(m.id, { auto: 4 })}>Auto-cycle 4 s</button>
            <button onclick={() => mock(m.id, { auto: 0 })}>Stop auto</button>
            <button onclick={() => mock(m.id, { connected: !m.connected })}>{m.connected ? 'Simulate disconnect' : 'Reconnect'}</button>
          </div>
        </div>
      {/each}
    </section>
  </div>
{/if}

<style>
  .dir { padding: 12px; padding-top: max(12px, env(safe-area-inset-top)); display: flex; flex-direction: column; gap: 12px; }
  header h2 { margin: 0; }
  .vol input { width: 120px; }
  .camOn { background: var(--program); border-color: var(--program); }
  .callStrip { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 10px; }
  .callTile { position: relative; aspect-ratio: 16 / 9; background: #000; border-radius: 10px; overflow: hidden; }
  .callTile.self :global(video) { transform: scaleX(-1); }
  .cname { position: absolute; left: 8px; bottom: 8px; background: rgba(0,0,0,.7); padding: 2px 8px; border-radius: 6px; font-weight: 700; }
  .talk .targets { margin-bottom: 12px; }
  .talkers { display: flex; flex-direction: column; gap: 6px; min-width: 200px; }
  .talker { background: var(--talk); color: #000; font-weight: 800; padding: 6px 12px; border-radius: 999px; align-self: flex-start; }
  .calls { display: flex; flex-direction: column; gap: 6px; }
  .callIn { background: var(--accent); color: #fff; font-weight: 800; border: 0; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
  .tile { background: var(--panel); border: 4px solid var(--line); border-radius: var(--radius); padding: 12px; display: flex; flex-direction: column; gap: 8px; }
  .t-program { border-color: var(--program); }
  .t-preview { border-color: var(--preview); }
  .t-unknown { border-color: #777; border-style: dashed; }
  .selected { box-shadow: 0 0 0 3px var(--accent); }
  .calling { animation: flash 0.8s steps(1) infinite; }
  .num { font-size: 1.8rem; font-weight: 900; }
  .pname { font-weight: 800; }
  .thumb { position: relative; aspect-ratio: 16 / 9; background: #000; border-radius: 8px; overflow: hidden; }
  .thumb .ns { position: absolute; inset: 0; display: grid; place-items: center; color: var(--program); font-weight: 900; background: #111; }
  .meta { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .who { font-size: 0.85rem; }
  .who.talking { background: var(--talk); color: #000; border-radius: 6px; padding: 0 6px; font-weight: 800; }
  .small { font-size: 0.8rem; }
  .q.ok { color: var(--preview); }
  .q.warn { color: var(--warn); }
  .q.bad { color: #ff6b6b; }
  .actions { margin-top: auto; }
  .prov { margin-bottom: 8px; }
  .mock { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
  .pgmOn { background: var(--program); border-color: var(--program); }
  .pvwOn { background: var(--preview); border-color: var(--preview); color: #000; }
</style>
